import { observer, useLocalObservable } from 'mobx-react';
import { useEffect } from 'react';
import { runInAction } from 'mobx';
import { useSearchParams } from 'react-router';
import useHistory from '../../hooks/useHistory';
import { useStore } from '../../store/store';
import Reroute from '../../components/navigation/Reroute';
import StaticPageLayout from '../../components/layouts/StaticPageLayout';
import IndividualDetails from '../../components/profiler/IndividualDetails/IndividualDetails';
import GeneralLoader from '../../components/loaders/GeneralLoader';
import { API_ENDPOINTS } from '../../api/endpoints';

/**
 * IndividualDetailsPage - Standalone page for individual details via URL navigation
 *
 * URL pattern: /en/individual-details/:individualId?ref=EntityName
 * - individualId: Individual ID in URL path
 * - ref: Back button label (query param)
 *
 * Fetches individual data from API using individualId.
 * Back navigation uses browser history with optional label from ref param.
 */
const IndividualDetailsPage = observer(() => {
  const { authStore, makeRequest } = useStore();
  const { params } = useHistory();
  const [searchParams] = useSearchParams();

  // Read URL parameters
  const individualId = params.individualId;
  const backRef = searchParams.get('ref');

  const state = useLocalObservable(() => ({
    // Track current individualId to detect URL changes
    currentIndividualId: individualId,
    // Individual data from API
    individual: null,
    isLoading: true,
    error: null
  }));

  // Reset state when individualId changes (handles navigation between individuals)
  useEffect(() => {
    if (individualId !== state.currentIndividualId) {
      runInAction(() => {
        state.currentIndividualId = individualId;
        state.individual = null;
        state.isLoading = true;
        state.error = null;
      });
    }
  }, [individualId, state]);

  // Fetch individual data from API
  useEffect(() => {
    if (!individualId) {
      runInAction(() => {
        state.isLoading = false;
        state.error = 'No individual ID provided';
      });
      return;
    }

    runInAction(() => {
      state.isLoading = true;
      state.error = null;
    });

    makeRequest({
      endpoint: API_ENDPOINTS.GET_PROFILER_PERSON_DETAILS,
      body: { personId: individualId },
      onSuccess: (data) => {
        runInAction(() => {
          state.individual = data;
          state.isLoading = false;
        });
      },
      onError: (error) => {
        console.error('Failed to fetch individual details:', error);
        runInAction(() => {
          state.isLoading = false;
          state.error = 'Unable to load individual details. Please try again.';
        });
      }
    });
  }, [individualId, makeRequest, state]);

  // Update page title
  useEffect(() => {
    const name = state.individual?.fullName || state.individual?.full_name || 'Individual details';
    document.title = `transact.digital GmbH - ${name}`;
  }, [state.individual]);

  // No individual ID in URL - redirect to profiler
  if (!individualId) {
    return <Reroute />;
  }

  // Show loader while fetching from API
  if (state.isLoading) {
    return (
      <StaticPageLayout page="projects-listing">
        <GeneralLoader />
      </StaticPageLayout>
    );
  }

  // Show error state
  if (state.error && !state.individual) {
    return (
      <StaticPageLayout page="projects-listing">
        <div className="error-message">{state.error}</div>
      </StaticPageLayout>
    );
  }

  // Show back button if:
  // 1. ref param is present (indicates we came from somewhere)
  // 2. There's actual browser history to go back to (not a new tab)
  // New tabs opened via right-click have history.length <= 2 (varies by browser)
  const hasHistory = typeof window !== 'undefined' && window.history.length > 2;
  const showBackButton = !!backRef && hasHistory;

  return (
    <StaticPageLayout page="projects-listing">
      <IndividualDetails
        individual={state.individual}
        project={{ ownerId: authStore.currentUserId }}
        hideBackButton={!showBackButton}
        backButtonLabel={backRef ? `Back to ${backRef}` : null}
      />
    </StaticPageLayout>
  );
});

export default IndividualDetailsPage;
